import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Users, UserPlus, Mail, Clock, CheckCircle, 
  Copy, Check, RefreshCw, TrendingUp, Building2
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { useSubdomain } from '../contexts/SubdomainContext';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

const MyReferrals = () => {
  const { user } = useAuth();
  const { company, isSubdomain } = useSubdomain();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await api.get('/invites/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch referral stats:', error);
      toast.error('Failed to load referral stats');
    } finally {
      setLoading(false);
    }
  };

  // Generate the referral link
  const getReferralLink = () => {
    const userId = user?.id;
    const companyId = user?.company_id;
    
    let baseUrl = 'https://www.trados.app';
    
    if (isSubdomain && company?.subdomain) {
      baseUrl = `https://${company.subdomain}.trados.app`;
    } else if (companyId && company?.subdomain) {
      baseUrl = `https://${company.subdomain}.trados.app`;
    }
    
    let link = `${baseUrl}/register`;
    const params = [];
    
    if (userId) {
      params.push(`ref_user=${userId}`);
    }
    if (companyId) {
      params.push(`ref_company=${companyId}`);
    }
    
    if (params.length > 0) {
      link += '?' + params.join('&');
    }
    
    return link;
  };

  const copyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(getReferralLink());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Referral link copied!');
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${BRAND_BLUE}15` }}>
                <Users className="w-5 h-5" style={{ color: BRAND_BLUE }} />
              </div>
              My Referrals
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Track your referrals and invite new users
            </p>
          </div>
          
          <button
            onClick={fetchStats}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
            data-testid="refresh-referrals"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <UserPlus className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">{stats?.total_referred || 0}</p>
                <p className="text-sm text-gray-500">Users Referred</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <Mail className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">{stats?.total_invites_sent || 0}</p>
                <p className="text-sm text-gray-500">Invites Sent</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">
                  {stats?.total_invites_sent > 0 
                    ? Math.round((stats?.total_referred / stats?.total_invites_sent) * 100) 
                    : 0}%
                </p>
                <p className="text-sm text-gray-500">Conversion Rate</p>
              </div>
            </div>
          </div>
        </div>

        {/* Referral Link Section */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <UserPlus className="w-5 h-5" style={{ color: BRAND_BLUE }} />
            Your Referral Link
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Share this link with others. When they sign up, they'll be tracked as your referral.
          </p>
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={getReferralLink()}
              readOnly
              className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 font-mono"
              data-testid="referral-link-input"
            />
            <button
              onClick={copyReferralLink}
              className="flex items-center gap-2 px-5 py-3 text-white rounded-xl font-medium text-sm transition hover:opacity-90"
              style={{ backgroundColor: BRAND_BLUE }}
              data-testid="copy-referral-link"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        {/* Referred Users List */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">People You've Referred</h2>
          </div>
          
          {stats?.referred_users?.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {stats.referred_users.map((referral, index) => (
                <div key={index} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                      {referral.name?.charAt(0)?.toUpperCase() || referral.email?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{referral.name || 'No name'}</p>
                      <p className="text-sm text-gray-500">{referral.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {referral.verified ? (
                      <span className="flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2.5 py-1 rounded-full">
                        <CheckCircle className="w-3 h-3" /> Verified
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full">
                        <Clock className="w-3 h-3" /> Pending
                      </span>
                    )}
                    {referral.signed_up_at && (
                      <span className="text-xs text-gray-400">
                        {new Date(referral.signed_up_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-6 py-16 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No referrals yet</h3>
              <p className="text-gray-500 text-sm mb-6">
                Share your referral link to start building your network
              </p>
              <button
                onClick={copyReferralLink}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-white rounded-xl font-medium text-sm transition hover:opacity-90"
                style={{ backgroundColor: BRAND_BLUE }}
              >
                <Copy className="w-4 h-4" />
                Copy Referral Link
              </button>
            </div>
          )}
        </div>
      </div>
    </Sidebar>
  );
};

export default MyReferrals;
